import React from 'react';
import type { IntlShape } from 'react-intl-next';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Text, Stack } from '@atlaskit/primitives/compiled';
import Select, { components } from '@atlaskit/select';
import type {
	FormatOptionLabelMeta,
	StylesConfig,
	ValueType,
	SingleValueProps,
	ActionMeta,
} from '@atlaskit/select';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Field } from '@atlaskit/form';
import type { SingleValue } from '@atlaskit/react-select';

import type {
	ConfluenceLength,
	ConfluenceTone,
} from './__generated__/CustomizePanelUpdateAudioPreferenceMutation.graphql.ts';

const i18n = defineMessages({
	audioToneLabel: {
		id: 'audio.audio-popup.customize-tab.tone-label',
		defaultMessage: 'How does the audio sound?',
		description: 'Label for a Dropdown component used for selecting audio tone',
	},
	professionalToneLabel: {
		id: 'audio.audio-popup.customize-tab.tone.professional',
		defaultMessage: 'Professional',
		description: 'Label for a Professional tone option in a Select component',
	},
	professionalToneDescription: {
		id: 'audio.audio-popup.customize-tab.tone.professional.description',
		defaultMessage: 'Clear and direct',
		description: 'Description for the Professional tone option',
	},
	conversationalToneLabel: {
		id: 'audio.audio-popup.customize-tab.tone.conversational',
		defaultMessage: 'Conversational',
		description: 'Label for a Conversational tone option in a Select component',
	},
	conversationalToneDescription: {
		id: 'audio.audio-popup.customize-tab.tone.conversational.description',
		defaultMessage: 'Approachable and informative',
		description: 'Description for the Conversational tone option',
	},
	playfulToneLabel: {
		id: 'audio.audio-popup.customize-tab.tone.playful',
		defaultMessage: 'Playful',
		description: 'Label for a Playful tone option in a Select component',
	},
	playfulToneDescription: {
		id: 'audio.audio-popup.customize-tab.tone.playful.description',
		defaultMessage: 'Lively and occasionally humorous',
		description: 'Description for the Playful tone option',
	},
	surpriseToneLabel: {
		id: 'audio.audio-popup.customize-tab.tone.surprise',
		defaultMessage: 'Surprise me',
		description: 'Label for a Surprise Me tone option in a Select component',
	},
	surpriseToneDescription: {
		id: 'audio.audio-popup.customize-tab.tone.surprise.description',
		defaultMessage: 'Takes on a unique personality',
		description: 'Description for the Surprise Me tone option',
	},
});

type ToneOption = {
	label: string;
	value: ConfluenceTone;
	description: string;
};

type ToneOptionProps = {
	option: ToneOption;
	meta: FormatOptionLabelMeta<ToneOption>;
	formatMessage: IntlShape['formatMessage'];
};

const styles = cssMap({
	wrap: {
		whiteSpace: 'break-spaces',
	},
});

const toneSelectStyles: StylesConfig<ToneOption, false> = {
	control: (style) => ({
		...style,
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
	}),
};

// Labels are purposely string values rather than Message objects
// This allows us to call formatMessage where needed
const toneOptions: ToneOption[] = [
	{
		label: 'professionalToneLabel',
		value: 'PROFESSIONAL' as ConfluenceTone,
		description: 'professionalToneDescription',
	},
	{
		label: 'conversationalToneLabel',
		value: 'CONVERSATIONAL' as ConfluenceTone,
		description: 'conversationalToneDescription',
	},
	{
		label: 'playfulToneLabel',
		value: 'PLAYFUL' as ConfluenceTone,
		description: 'playfulToneDescription',
	},
	{
		label: 'surpriseToneLabel',
		value: 'SURPRISE' as ConfluenceTone,
		description: 'surpriseToneDescription',
	},
];

const ToneOptionComponent = ({ option, meta: { context }, formatMessage }: ToneOptionProps) => {
	if (context === 'menu') {
		return (
			<Stack>
				<Text color="color.text.subtle" weight="semibold" size="small">
					{formatMessage(i18n[option.label])}
				</Text>
				<Text color="color.text.subtlest" size="small">
					{formatMessage(i18n[option.description])}
				</Text>
			</Stack>
		);
	}
	return formatMessage(i18n[option.label]);
};

const ToneValue = ({ data, ...props }: SingleValueProps<ToneOption, false>) => {
	const { formatMessage } = useIntl();

	return (
		<components.SingleValue data={data} {...props}>
			<Box paddingBlock="space.050" paddingInlineEnd="space.075">
				<Box>
					<Text color="color.text.subtle" weight="semibold" size="small">
						{formatMessage(i18n[data.label])}
					</Text>
				</Box>
				<Box xcss={styles.wrap}>
					<Text color="color.text.subtlest" size="small">
						{formatMessage(i18n[data.description])}
					</Text>
				</Box>
			</Box>
		</components.SingleValue>
	);
};

interface ToneSelectorProps {
	onPreferenceUpdate: (
		updatedPreferences: Partial<{
			length: ConfluenceLength;
			tone: ConfluenceTone;
			playbackSpeed: number;
		}>,
	) => void;
	defaultValue: ConfluenceTone | null;
}

export const ToneSelector = ({ onPreferenceUpdate, defaultValue }: ToneSelectorProps) => {
	const { formatMessage } = useIntl();

	// Convert ConfluenceTone to ToneOption for Field component
	const defaultToneOption = defaultValue
		? toneOptions.find((option) => option.value === defaultValue) || null
		: null;

	return (
		<Field<ValueType<ToneOption, false>>
			name="tone"
			label={formatMessage(i18n.audioToneLabel)}
			defaultValue={defaultToneOption}
		>
			{({ fieldProps: { id, value: fieldValue, onChange, ...rest } }) => {
				// Use field value if available, otherwise fall back to defaultToneOption
				const currentSelectedOption = fieldValue || defaultToneOption;

				return (
					<Box paddingBlockStart="space.100">
						<Select
							inputId={id}
							{...rest}
							value={currentSelectedOption}
							formatOptionLabel={(option, meta) => (
								<ToneOptionComponent option={option} meta={meta} formatMessage={formatMessage} />
							)}
							options={toneOptions}
							components={{
								SingleValue: ToneValue,
							}}
							isSearchable={false}
							menuShouldScrollIntoView
							// Select is a unique case where this is semantically correct
							// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
							styles={toneSelectStyles}
							menuPosition="fixed"
							onChange={(
								selectedOption: SingleValue<ToneOption>,
								_actionMeta: ActionMeta<ToneOption>,
							) => {
								if (selectedOption) {
									onChange(selectedOption);
									onPreferenceUpdate({
										tone: selectedOption.value,
									});
								}
							}}
						/>
					</Box>
				);
			}}
		</Field>
	);
};
